import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, deriveAccessLevel, type AccessLevel, type PortalUser } from './api';
import { queryClient } from './queries';

type AuthState = {
  user: PortalUser | null;
  accessLevel: AccessLevel;
  loading: boolean;
  authError: string;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PortalUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');
  const loadedOnce = useRef(false);

  const refresh = async () => {
    if (!loadedOnce.current) setLoading(true);
    try {
      const nextUser = await api.me({ redirectOnAuthError: false });
      setUser(nextUser);
      setAuthError('');
      queryClient.setQueryData(['me'], nextUser);
    } catch (err) {
      setUser(null);
      setAuthError(err instanceof Error ? err.message : '');
      queryClient.removeQueries({ queryKey: ['me'] });
    } finally {
      loadedOnce.current = true;
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const value = useMemo(
    () => ({
      user,
      accessLevel: deriveAccessLevel(user),
      loading,
      authError,
      refresh,
    }),
    [authError, loading, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
